import axios from "axios";
import { API_URL } from "./settings";

// Centralized axios singleton with automatic token refresh & queueing
let axiosSingleton = null;
let isRefreshing = false;
let refreshQueue = [];

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem("loggedAppUser");
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};
const getToken = () => getStoredUser()?.token || null;

const setToken = (newToken) => {
  const raw = localStorage.getItem("loggedAppUser");
  if (!raw) return;
  try {
    const data = JSON.parse(raw);
    data.token = newToken;
    localStorage.setItem("loggedAppUser", JSON.stringify(data));
  } catch (error) {
    console.log("Error updating stored user session:", error);
  }
};

const failAllQueued = (err) => {
  refreshQueue.forEach((p) => p.reject(err));
  refreshQueue = [];
};
const resolveAllQueued = (token) => {
  refreshQueue.forEach((p) => p.resolve(token));
  refreshQueue = [];
};

const createAxios = () => {
  const instance = axios.create({
    baseURL: API_URL,
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
  });

  instance.interceptors.request.use((config) => {
    if (config.url && !config.url.startsWith('/api/') && !config.url.startsWith('http')) {
      const baseHasApiPrefix = config.baseURL &&
        (config.baseURL.endsWith('/api') || config.baseURL.endsWith('/api/'));
      if (!baseHasApiPrefix) {
        config.url = `/api/${config.url.replace(/^\//, '')}`;
      }
    }
    return config;
  });

  // Request: always attach latest token
  instance.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
    return config;
  });

  // Response: handle token expiration
  instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const originalRequest = error.config;
      const isExpiredResponse =
        error?.response?.status === 401 &&
        error?.response?.data?.code === "TokenExpired";
      if (!isExpiredResponse || originalRequest._retry) {
        return Promise.reject(error);
      }

      // Queue retry if already refreshing
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          originalRequest._retry = true;
          return instance(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const current = getToken();        
        const { data } = await axios.post(
          `${API_URL}/api/v2/Auth/Refresh`,
          { token: current },
          {
            headers: { "Content-Type": "application/json" },
            withCredentials: false,
          },
        );
        const newToken = data?.newToken;
        if (!newToken) throw new Error("Missing newToken in refresh response");        
        setToken(newToken);
        resolveAllQueued(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return instance(originalRequest);
      } catch (refreshErr) {        
        failAllQueued(refreshErr);
        localStorage.removeItem("loggedAppUser");
        window.location.href = "/";
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    },
  );

  return instance;
};

const axiosInstance = () => {
  if (!axiosSingleton) axiosSingleton = createAxios();
  return axiosSingleton;
};

export default axiosInstance;
